import React, { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const STATUS_LABELS = {
  1: "Not Paid",
  2: "Paid",
  3: "Accepted",
  4: "In Transit",
  5: "Delivered",
  6: "Rejected",
  7: "Refunded",
  8: "Return Requested",
  9: "Return In Transit",
  10: "Returned",
  11: "Return Rejected"
};

export const OrderPage = ({ fetchOrder, order, isLoading }) => {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [trackingData, setTrackingData] = useState(null);
  const [trackingError, setTrackingError] = useState("");
  const [loadingTracking, setLoadingTracking] = useState(false);

  // Review & Rating State
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewedProductIds, setReviewedProductIds] = useState([]);

  // Live delivery polling state
  const prevStatusRef = useRef(null);
  const pollingRef = useRef(null);
  const dismissedReviewRef = useRef(false);

  // Return request state
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [submittingReturn, setSubmittingReturn] = useState(false);
  const [upiId, setUpiId] = useState("");

    useEffect(() => {
      if(id) {
        dismissedReviewRef.current = false;
        prevStatusRef.current = null;
        fetchOrder(id);
      }
    }, [id]);

    // Pre-fetch reviews to check which products are already reviewed
    useEffect(() => {
      if (order && Number(order.id) === Number(id) && order.status == 5) {
        const cartItems = JSON.parse(order.cart || "[]");
        const userLogged = JSON.parse(sessionStorage.getItem("loggedUser") || "{}");
        if (userLogged?.email) {
          cartItems.forEach(item => {
            const pId = item.ProductId || item.Product?.id;
            if (pId) {
              axios.get(`${process.env.REACT_APP_API_URL}/review/product/${pId}`)
                .then(res => {
                  const reviews = res.data.data || [];
                  const alreadyReviewed = reviews.some(r => r.User?.email?.toLowerCase() === userLogged.email.toLowerCase());
                  if (alreadyReviewed) {
                    setReviewedProductIds(prev => [...prev, Number(pId)]);
                  }
                })
                .catch(err => console.error("Error pre-fetching review status:", err));
            }
          });
        }
      }
    }, [order, id]);

    // Auto-open review modal when delivery is confirmed (via polling)
    useEffect(() => {
      if (dismissedReviewRef.current) return;
      if (order && Number(order.id) === Number(id)) {
        if (prevStatusRef.current === 4 && order.status === 5) {
          // Order just got delivered! Show review popup
          const cartItems = JSON.parse(order.cart || "[]");
          const firstUnreviewed = cartItems.find(item => {
            const pId = Number(item.ProductId || item.Product?.id);
            return !reviewedProductIds.includes(pId);
          });
          if (firstUnreviewed?.Product) {
            setTimeout(() => {
              setActiveProduct(firstUnreviewed.Product);
              setReviewModalOpen(true);
            }, 800);
          }
        }
        prevStatusRef.current = order.status;
      }
    }, [order?.status, id]);

    // Live polling: when order is In Transit, poll every 12s to detect delivery
    useEffect(() => {
      if (order?.status === 4) {
        pollingRef.current = setInterval(() => {
          fetchOrder(id);
        }, 12000);
      } else {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }
      return () => {
        if (pollingRef.current) clearInterval(pollingRef.current);
      };
    }, [order?.status, id]);

    const handleReviewSubmit = () => {
      if (!rating || rating < 1 || rating > 5) {
        toast.error("Please select a rating between 1 and 5.");
        return;
      }
      setSubmittingReview(true);
      axios.post(
        `${process.env.REACT_APP_API_URL}/review`,
        {
          rating,
          comment,
          ProductId: activeProduct.id,
          OrderId: order.id
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('userToken')}`
          }
        }
      )
      .then((response) => {
        toast.success("Review submitted successfully! Thank you.");
        dismissedReviewRef.current = true;
        setReviewedProductIds([...reviewedProductIds, Number(activeProduct.id)]);
        setReviewModalOpen(false);
        setActiveProduct(null);
        setComment("");
        setRating(5);
      })
      .catch((err) => {
        console.error(err);
        const msg = err.response?.data?.message || "Failed to submit review.";
        toast.error(msg);
      })
      .finally(() => {
        setSubmittingReview(false);
      });
    };

    const handleReturnSubmit = () => {
      if (!returnReason.trim()) {
        toast.error("Please enter a reason for returning.");
        return;
      }
      if (order?.orderType === 2 && !upiId.trim()) {
        toast.error("Please enter a valid UPI ID for the refund.");
        return;
      }
      setSubmittingReturn(true);
      axios.post(
        `${process.env.REACT_APP_API_URL}/order/${order.id}/return-request`,
        { 
          reason: returnReason,
          ...(order?.orderType === 2 && { upiId })
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('userToken')}`
          }
        }
      )
      .then((response) => {
        toast.success("Return request submitted successfully!");
        setReturnModalOpen(false);
        setReturnReason("");
        setUpiId("");
        fetchOrder(id);
      })
      .catch((err) => {
        console.error(err);
        const msg = err.response?.data?.message || "Failed to submit return request.";
        toast.error(msg);
      })
      .finally(() => {
        setSubmittingReturn(false);
      });
    };

    const handleCancelOrder = () => {
      if (window.confirm("Are you sure you want to cancel this order?")) {
        axios.post(
          `${process.env.REACT_APP_API_URL}/order/${order.id}/cancel`,
          {},
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem('userToken')}`
            }
          }
        )
        .then((response) => {
          toast.success("Order cancelled successfully!");
          fetchOrder(id);
        })
        .catch((err) => {
          console.error(err);
          const msg = err.response?.data?.message || "Failed to cancel order.";
          toast.error(msg);
        });
      }
    };

    useEffect(() => {
      const hasTracking = order && order.status !== 6 && order.status !== 7 && (order.status >= 8 ? order.returnTrackingId : order.trackingId);
      if (hasTracking) {
        setLoadingTracking(true);
        setTrackingError("");
        const isReturn = order.status >= 8;
        axios.get(`${process.env.REACT_APP_API_URL}/order/${order.id}/track`, {
          params: { type: isReturn ? 'return' : 'standard' }
        })
          .then((response) => {
            setTrackingData(response.data.data);
            setLoadingTracking(false);
          })
          .catch((err) => {
            console.error("Failed to load tracking data:", err);
            setTrackingError("Unable to retrieve live tracking updates at this moment.");
            setLoadingTracking(false);
          });
      }
    }, [order]);

    const contentRef = useRef(null);
    const handlePrint =  useReactToPrint({ contentRef });
    const cart = JSON.parse(order?.cart || "[]");

  return (
    <>
      <div className="full-row bg-light overlay-dark py-5"
        style={{backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/banner.png)`, backgroundPosition: "center center", backgroundSize: "cover"}}>
        <div className="container">
          <div className="row text-center text-white">
            <div className="col-12">
              <h3 className="mb-2 text-white font-weight-bold">User Orders</h3>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb justify-content-center bg-transparent">
                  <li className="breadcrumb-item">
                    <a href="#" className="text-white">Home</a>
                  </li>
                  <li className="breadcrumb-item active text-white" aria-current="page">User Orders</li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </div>
      <main>
        <div class="my-4 px-3" style={{ maxWidth: "1000px", margin: "auto" }}>
            <div class="container">
                <div class="row">
                    <div class="col-12 d-md-none mb-3">
                        <button class="btn btn-dark d-inline-flex align-items-center gap-2" data-bs-toggle="offcanvas" data-bs-target="#offcanvasSidebar" aria-controls="offcanvasSidebar">
                            <i class="bi bi-list"></i> Menu
                        </button>
                    </div>
            
                    <div className="offcanvas offcanvas-start d-md-none" id="offcanvasSidebar" tabIndex="-1" aria-labelledby="offcanvasSidebarLabel">
                        <div class="offcanvas-header">
                            <h5 class="offcanvas-title" id="offcanvasSidebarLabel">Dashboard</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
                        </div>
                        <div class="offcanvas-body">
                            <div class="box1-list">
                                <h6 style={{ cursor: "pointer" }} onClick={() => navigate("/orders")}>Orders</h6>
                                <hr />
                                <h6 style={{ cursor: "pointer" }} onClick={() => navigate("/profile")}>Edit Profile</h6>
                                <hr />
                                <h6 style={{ cursor: "pointer" }} onClick={() => navigate("/update-password")}>Change Passwords</h6>
                                <hr />
                                <h6 style={{ cursor: "pointer" }} onClick={() => {
                                    sessionStorage.removeItem("loggedUser");
                                    sessionStorage.removeItem("userToken");
                                    window.location.href = "/login"
                                }}>Logout</h6>
                            </div>
                        </div>
                    </div>
            
                    <div class="col-md-4 d-none d-md-block">
                        <div class="box1">
                            <div class="heading">
                                <h3 class="profile-heading">
                                    Orders
                                    <span style={{ display: "inline-block", borderBottom: "3px solid black", width: "12%", textAlign: "left", marginTop: "10px"}}>
                                    </span>
                                </h3>
                            </div>
                            <div class="box1-list">
                                <h6 style={{ cursor: "pointer" }} onClick={() => navigate("/orders")}>Orders</h6>
                                <hr />
                                <h6 style={{ cursor: "pointer" }} onClick={() => navigate("/profile")}>Edit Profile</h6>
                                <hr />
                                <h6 style={{ cursor: "pointer" }} onClick={() => navigate("/update-password")}>Change Passwords</h6>
                                <hr />
                                <h6 style={{ cursor: "pointer" }} onClick={() => {
                                    sessionStorage.removeItem("loggedUser");
                                    sessionStorage.removeItem("userToken");
                                    window.location.href = "/login"
                                }}>Logout</h6>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-8">
                        <h3 class="profile-heading">Order
                            <span style={{ display: "inline-block", borderBottom: "3px solid black", width: "6%", textAlign: "left", marginTop: "10px"}}>
                            </span>
                        </h3>
                        {/* <div class="align-items-center upload-image">
                            <img src="./images/profile.jpg" alt="Profile Picture" class="rounded-circle img-thumbnail me-3" width="135px" />
                            <button class="btn btn-dark upload-btn">Upload</button>
                        </div> */}
                        <div class="print-order text-right">
                            <p onClick={() => handlePrint()} class="print-order-btn" style={{ cursor: "pointer" }}>
                                <i class="fa fa-print"></i> Print Order
                            </p>
                        </div>
                        {order?.status === 6 ? (
                            <div className="process-steps-area" style={{ marginBottom: '40px' }}>
                                <ul className="process-steps d-flex justify-content-center">
                                    <li className="active" style={{ width: '50%', float: 'none' }}>
                                        <div className="icon">1</div>
                                        <div className="title">Order Placed</div>
                                    </li>
                                    <li className="active-rejected" style={{ width: '50%', float: 'none' }}>
                                        <div className="icon-rejected"><i className="fa-solid fa-xmark"></i></div>
                                        <div className="title">Rejected</div>
                                    </li>
                                </ul>
                                <style>{`
                                    .process-steps li.active-rejected .icon-rejected {
                                        height: 30px;
                                        width: 30px;
                                        margin: auto;
                                        background: #dc3545;
                                        border-radius: 50%;
                                        line-height: 30px;
                                        font-size: 14px;
                                        font-weight: 700;
                                        color: #fff;
                                        position: relative;
                                        z-index: 1;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                    }
                                    .process-steps li.active-rejected .title {
                                        font-weight: 600;
                                        font-size: 13px;
                                        color: #dc3545;
                                        margin-top: 8px;
                                    }
                                    .process-steps li.active-rejected:after {
                                        position: absolute;
                                        content: "";
                                        height: 3px;
                                        width: calc(100% - 30px);
                                        background: #dc3545;
                                        top: 21px;
                                        z-index: 0;
                                        right: calc(50% + 15px);
                                    }
                                `}</style>
                            </div>
                        ) : order?.status === 7 ? (
                            <div className="process-steps-area" style={{ marginBottom: '40px' }}>
                                <ul className="process-steps d-flex justify-content-center">
                                    <li className="active" style={{ width: '50%', float: 'none' }}>
                                        <div className="icon">1</div>
                                        <div className="title">Order Placed</div>
                                    </li>
                                    <li className="active-refunded" style={{ width: '50%', float: 'none' }}>
                                        <div className="icon-refunded"><i className="fa-solid fa-indian-rupee-sign"></i></div>
                                        <div className="title">Refunded</div>
                                    </li>
                                </ul>
                                <style>{`
                                    .process-steps li.active-refunded .icon-refunded {
                                        height: 30px;
                                        width: 30px;
                                        margin: auto;
                                        background: #475569;
                                        border-radius: 50%;
                                        line-height: 30px;
                                        font-size: 14px;
                                        font-weight: 700;
                                        color: #fff;
                                        position: relative;
                                        z-index: 1;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                    }
                                    .process-steps li.active-refunded .title {
                                        font-weight: 600;
                                        font-size: 13px;
                                        color: #475569;
                                        margin-top: 8px;
                                    }
                                    .process-steps li.active-refunded:after {
                                        position: absolute;
                                        content: "";
                                        height: 3px;
                                        width: calc(100% - 30px);
                                        background: #475569;
                                        top: 21px;
                                        z-index: 0;
                                        right: calc(50% + 15px);
                                    }
                                `}</style>
                            </div>
                        ) : (
                            <div className="process-steps-area">
                                <ul className="process-steps">
                                    <li className={order?.status >= 1 ? "active" : ""}>
                                        <div className="icon">1</div>
                                        <div className="title">Order Placed</div>
                                    </li>
                                    <li className={(order?.status >= 2 && order?.status !== 6 && order?.status !== 7) ? "active" : ""}>
                                        <div className="icon">2</div>
                                        <div className="title">Accepted</div>
                                    </li>
                                    <li className={(order?.status >= 4 && order?.status !== 6 && order?.status !== 7) ? "active" : ""}>
                                        <div className="icon">3</div>
                                        <div className="title">In Transit</div>
                                    </li>
                                    <li className={order?.status === 5 ? "active" : ""}>
                                        <div className="icon">4</div>
                                        <div className="title">Delivered</div>
                                    </li>
                                </ul>
                            </div>
                        )}

                        {/* Order Rejected Banner */}
                        {order?.status === 6 && (
                          <div className="order-banner-card rejected">
                            <div style={{ fontSize: '30px' }}>🚫</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 800, fontSize: '17px', marginBottom: '4px' }}>Order Rejected</div>
                              <div style={{ fontSize: '13px', opacity: 0.9 }}>This order has been rejected/cancelled. If you have paid, your refund will be processed back to your original payment method.</div>
                            </div>
                          </div>
                        )}

                        {/* Order Refunded Banner */}
                        {order?.status === 7 && (
                          <div className="order-banner-card" style={{ backgroundColor: '#F1F5F9', borderColor: '#CBD5E1', borderLeftColor: '#475569' }}>
                            <div style={{ fontSize: '30px' }}>💰</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 800, fontSize: '17px', marginBottom: '4px', color: '#1E293B' }}>Order Refunded</div>
                              <div style={{ fontSize: '13px', color: '#475569' }}>This order has been cancelled and refunded. The refund amount has been credited back to your account.</div>
                            </div>
                          </div>
                        )}

                        {/* Return Status Banners */}
                        {order?.status === 8 && (
                          <div className="order-banner-card info">
                            <div style={{ fontSize: '30px' }}>↩️</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 800, fontSize: '17px', marginBottom: '4px' }}>Return Requested</div>
                              <div style={{ fontSize: '13px', opacity: 0.9 }}>
                                Your return request is currently pending approval. We are reviewing your reason: <strong>"{order.returnReason || 'No reason specified'}"</strong>.
                              </div>
                            </div>
                          </div>
                        )}

                        {order?.status === 9 && (
                          <div className="order-banner-card warning">
                            <div style={{ fontSize: '30px' }}>📦</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 800, fontSize: '17px', marginBottom: '4px' }}>Return In Transit</div>
                              <div style={{ fontSize: '13px', opacity: 0.9 }}>
                                The return shipment has been dispatched and is in transit back to our warehouse. Reason: <strong>"{order.returnReason || 'No reason specified'}"</strong>.
                              </div>
                            </div>
                          </div>
                        )}

                        {order?.status === 10 && (
                          <div className="order-banner-card success">
                            <div style={{ fontSize: '30px' }}>✅</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 800, fontSize: '17px', marginBottom: '4px' }}>Order Returned & Completed</div>
                              <div style={{ fontSize: '13px', opacity: 0.9 }}>
                                The returned items have been successfully received and inspected. The return process is complete.
                              </div>
                            </div>
                          </div>
                        )}

                        {order?.status === 11 && (
                          <div className="order-banner-card rejected">
                            <div style={{ fontSize: '30px' }}>❌</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 800, fontSize: '17px', marginBottom: '4px' }}>Return Request Rejected</div>
                              <div style={{ fontSize: '13px', opacity: 0.9 }}>
                                Your return request has been rejected. If you have any queries, please contact our support.
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Cancel Order Banner (shown if status is Placed (1) or Accepted (2)) */}
                        {(order?.status === 1 || order?.status === 2) && (
                          <div className="order-banner-card action-banner">
                            <div style={{ flex: 1, minWidth: '250px' }}>
                              <div style={{ fontWeight: 800, fontSize: '17px', marginBottom: '4px', color: '#333' }}>Need to cancel this order?</div>
                              <div style={{ fontSize: '13px', color: '#666' }}>
                                You can cancel this order before it is dispatched.
                              </div>
                            </div>
                            <button 
                              className="btn btn-danger fw-bold text-white px-4" 
                              style={{ height: '44px', borderRadius: '8px', boxShadow: '0 2px 6px rgba(220, 53, 69, 0.2)' }}
                              onClick={handleCancelOrder}
                            >
                              Cancel Order
                            </button>
                          </div>
                        )}

                        {/* Request Return Banner (shown if status is Delivered) */}
                        {order?.status === 5 && (
                          <div className="order-banner-card action-banner">
                            <div style={{ flex: 1, minWidth: '250px' }}>
                              <div style={{ fontWeight: 800, fontSize: '17px', marginBottom: '4px', color: '#333' }}>Want to return this order?</div>
                              <div style={{ fontSize: '13px', color: '#666' }}>
                                You can request a return if you received a damaged, incorrect, or defective item.
                              </div>
                            </div>
                            <button 
                              className="btn btn-warning fw-bold text-white px-4" 
                              style={{ height: '44px', borderRadius: '8px', boxShadow: '0 2px 6px rgba(255, 193, 7, 0.2)' }}
                              onClick={() => setReturnModalOpen(true)}
                            >
                              Request Return
                            </button>
                          </div>
                        )}

                         {/* Out for Delivery Banner — Swiggy style (shown when In Transit) */}
                         {order?.status === 4 && (
                           <div className="order-banner-card delivery-alert">
                             <style>{`
                               @keyframes pulseGlow {
                                 0%, 100% { box-shadow: 0 6px 24px rgba(240,78,45,0.3); }
                                 50% { box-shadow: 0 8px 32px rgba(240,78,45,0.55); }
                               }
                               @keyframes truckMove {
                                 0%, 100% { transform: translateX(0px); }
                                 50% { transform: translateX(6px); }
                               }
                             `}</style>
                             <div style={{ fontSize: '42px', animation: 'truckMove 1.5s ease-in-out infinite' }}>🛵</div>
                             <div style={{ flex: 1 }}>
                               <div style={{ fontWeight: 800, fontSize: '17px', marginBottom: '4px' }}>Out for Delivery!</div>
                               <div style={{ fontSize: '13px', opacity: 0.92 }}>Aapka parcel raste mein hai aur jald hi aapke pass pahunchne wala hai.</div>
                             </div>
                             <div style={{ textAlign: 'center', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', padding: '10px 14px', minWidth: '80px' }}>
                               <div style={{ fontSize: '10px', opacity: 0.8, marginBottom: '2px' }}>AUTO REFRESH</div>
                               <div style={{ fontSize: '11px', fontWeight: 700 }}>Every 12s</div>
                             </div>
                           </div>
                         )}

                        {/* Live Shipment Tracking Timeline */}
                        {order?.status !== 6 && order?.status !== 7 && ((order?.status >= 8 && order?.returnTrackingId) || (order?.status < 8 && order?.trackingId)) && (
                          <div className="widget border-0 p-40 widget_categories bg-light account-info mb-4">
                            <h5 className="widget-title down-line mb-3" style={{ fontWeight: 'bold' }}>
                              <i className="fa-solid fa-truck-fast me-2 text-primary"></i> {order?.status >= 8 ? "Live Return Tracking" : "Live Shipment Tracking"}
                            </h5>
                            <div className="p-3 bg-white rounded shadow-sm border mb-3">
                              <div className="row">
                                <div className="col-sm-6 mb-2 mb-sm-0">
                                  <span className="text-muted small d-block">{order?.status >= 8 ? "Return Courier Partner" : "Courier Partner"}</span>
                                  <strong>{trackingData?.courierPartner || (order?.status >= 8 ? order?.returnCourierPartner : order?.courierPartner) || "Standard Courier"}</strong>
                                </div>
                                <div className="col-sm-6">
                                  <span className="text-muted small d-block">{order?.status >= 8 ? "Return Tracking ID / AWB" : "Tracking ID / AWB"}</span>
                                  <strong>{order?.status >= 8 ? order?.returnTrackingId : order?.trackingId}</strong>
                                </div>
                              </div>
                            </div>
                            
                            {loadingTracking ? (
                              <div className="text-center py-4">
                                <div className="spinner-border text-primary spinner-border-sm me-2" role="status"></div>
                                <span className="text-secondary small">Fetching live location updates...</span>
                              </div>
                            ) : trackingError ? (
                              <div className="alert alert-warning py-2 px-3 small">
                                <i className="fa-solid fa-circle-info me-2"></i>{trackingError}
                              </div>
                            ) : trackingData?.timeline && trackingData.timeline.length > 0 ? (
                              <div className="tracking-timeline mt-4" style={{ position: 'relative', paddingLeft: '30px' }}>
                                <style>{`
                                  .timeline-step {
                                    position: relative;
                                    padding-bottom: 25px;
                                  }
                                  .timeline-step:last-child {
                                    padding-bottom: 0;
                                  }
                                  .timeline-step::before {
                                    content: '';
                                    position: absolute;
                                    left: -20px;
                                    top: 6px;
                                    width: 2px;
                                    height: 100%;
                                    background-color: #e9ecef;
                                    z-index: 1;
                                  }
                                  .timeline-step:last-child::before {
                                    display: none;
                                  }
                                  .timeline-badge {
                                    position: absolute;
                                    left: -26px;
                                    top: 4px;
                                    width: 14px;
                                    height: 14px;
                                    border-radius: 50%;
                                    background-color: #dee2e6;
                                    border: 3px solid white;
                                    z-index: 2;
                                    box-shadow: 0 0 0 1px rgba(0,0,0,0.1);
                                  }
                                  .timeline-step.completed .timeline-badge {
                                    background-color: #198754;
                                  }
                                  .timeline-step.active .timeline-badge {
                                    background-color: #0d6efd;
                                    box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.25);
                                  }
                                `}</style>
                                {trackingData.timeline.map((step, idx) => {
                                  const isActive = step.completed && (idx === trackingData.timeline.filter(s => s.completed).length - 1);
                                  return (
                                    <div key={idx} className={`timeline-step ${step.completed ? 'completed' : ''} ${isActive ? 'active' : ''}`}>
                                      <div className="timeline-badge"></div>
                                      <div className="d-flex justify-content-between align-items-baseline">
                                        <h6 className={`mb-1 fw-bold ${isActive ? 'text-primary' : step.completed ? 'text-dark' : 'text-muted'}`}>
                                          {step.status}
                                        </h6>
                                        <span className="small text-muted" style={{ fontSize: '11px' }}>{step.time}</span>
                                      </div>
                                      <p className="mb-0 small text-secondary">{step.description}</p>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        )}

                        <div class="widget border-0 p-40 widget_categories bg-light account-info" ref={contentRef}>
                            <style>{`
                              @media print {
                                body {
                                  background: white !important;
                                  color: black !important;
                                  font-size: 11px !important;
                                }
                                .back-btn, .print-order, .no-print, header, footer, nav, aside, button {
                                  display: none !important;
                                }
                                .widget {
                                  border: none !important;
                                  padding: 0 !important;
                                  box-shadow: none !important;
                                  background: white !important;
                                }
                                .view-order-page {
                                  padding: 0 !important;
                                }
                                .table th {
                                  background-color: #f0f0f0 !important;
                                  -webkit-print-color-adjust: exact;
                                  print-color-adjust: exact;
                                }
                              }
                            `}</style>
                            {/* <h4 class="widget-title down-line mb-30">Purchased Items</h4> */}
                            <div class="view-order-page">
                                <h3 className="order-code">Order# BH{order?.id} [{STATUS_LABELS[order?.status] || "Lost"}]</h3>
                                <p class="order-date">Order Date {order?.createdAt}</p>
                                <div class="shipping-add-area">
                                    <div class="row">
                                        <div class="col-md-6">
                                            <h5>Shipping Address</h5>
                                            <address>
                                                Name: {order?.shippingName}<br />
                                                Email: {order.shippingEmail}<br />
                                                Phone: {order.shippingPhone}<br />
                                                Address: {order.shippingAddress}<br />
                                                {order?.shippingCity}&nbsp;{order?.shippingState}-{order.shippingZip}
                                            </address>
                                        </div>
                                        <div class="col-md-6">
                                            <h5>Shipping Method</h5>
                                            <p>Ship To Address</p>
                                        </div>
                                    </div>
                                </div>
                                <div class="billing-add-area">
                                    <div class="row">
                                        <div class="col-md-6">
                                            <h5>Billing Address</h5>
                                            <address>
                                                Name: {order?.billingName}<br />
                                                Email: {order.billingEmail}<br />
                                                Phone: {order.billingPhone}<br />
                                                Address: {order.billingAddress}<br />
                                                {order?.billingCity}&nbsp;{order?.billingState}-{order.billingZip}
                                            </address>
                                        </div>
                                    <div class="col-md-6">
                                        <h5>Payment Information</h5>
                                        <p>Payment Status : {order?.status == 1 ? <strong class="text-danger">UnPaid</strong> :<strong class="text-success">Paid</strong>}</p>
                                        <p>Tax: ₹{order?.gstAmount?.toFixed(2)}</p>
                                        <p>Shipping: ₹{order?.shippingAmount?.toFixed(2)}</p>
                                        <p>Discount: ₹{order?.discount?.toFixed(2)}</p>
                                        <p>Paid Amount: ₹{order?.finalAmount?.toFixed(2)}</p>
                                        <p>Payment Method: {order?.orderType === 2 ? 'Cash on Delivery (COD)' : 'Online Payment'}< br/>
                                        {order?.transactionId}</p>
                                    </div>
                                </div>
                            </div>
                            <br />
                            <div class="table-responsive">
                                <h5>Ordered Products:</h5>
                                <table class="table table-bordered">
                                    <thead>
                                        <tr class="tr-borders">
                                            <th style={{ minWidth: "60px" }}>SR No</th>
                                            <th style={{ minWidth: "150px" }}>Item & Description</th>
                                            <th style={{ minWidth: "80px" }}>HSN</th>
                                            <th style={{ minWidth: "60px" }}>Qty</th>
                                            <th style={{ minWidth: "110px" }}>Product Rate</th>
                                            <th style={{ minWidth: "80px" }}>Disc.</th>
                                            <th style={{ minWidth: "110px" }}>Taxable Amt.</th>
                                            <th style={{ minWidth: "70px" }}>CGST</th>
                                            <th style={{ minWidth: "90px" }}>CGST Amt.</th>
                                            <th style={{ minWidth: "70px" }}>S/UT GST</th>
                                            <th style={{ minWidth: "90px" }}>S/UT GST Amt.</th>
                                            <th style={{ minWidth: "100px" }}>Total Amt.</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    {Array.isArray(cart) && cart?.map((x, i) => {
                                        const productPrice = x?.Product?.currentPrice * x?.quantity;
                                        const gstRate = parseInt(x?.Product?.gst || 0);
                                        const productRealPrice = parseFloat(x?.Product?.currentPrice || 0);
                                        const productDiscount = productPrice * (order?.discountPercent / 100);
                                        const productRealSellingPrice = productPrice - productDiscount;
                                        const priceWithoutGst = productRealSellingPrice;
                                        const rowGstTotal = priceWithoutGst * (gstRate / 100);
                                        const rowTotal = priceWithoutGst + rowGstTotal;
                                        return (
                                        <tr class="tr-borders" key={i}>
                                            <td>{i + 1}</td>
                                            <td>
                                                <div>{x?.Product?.name}</div>
                                                {order?.status == 5 && (
                                                    reviewedProductIds.includes(Number(x?.Product?.id || x?.ProductId)) ? (
                                                        <span className="badge bg-secondary mt-1 text-white" style={{ fontSize: '10px', padding: '2px 6px' }}>Reviewed</span>
                                                    ) : (
                                                        <button 
                                                            className="btn btn-success mt-2 fw-bold text-white d-inline-flex align-items-center gap-1 border-0"
                                                            style={{ 
                                                                fontSize: '13px', 
                                                                padding: '6px 12px', 
                                                                backgroundColor: '#66A96B', 
                                                                boxShadow: '0 2px 4px rgba(102, 169, 107, 0.2)',
                                                                borderRadius: '4px'
                                                            }}
                                                            onClick={() => {
                                                                setActiveProduct(x?.Product);
                                                                setReviewModalOpen(true);
                                                            }}
                                                        >
                                                            ★ Write Review
                                                        </button>
                                                    )
                                                )}
                                            </td>
                                            <td>{x?.Product?.hsn}</td>
                                            <td>{x?.quantity}</td>
                                            <td>{productRealPrice.toFixed(2)}</td>
                                            <td>{productDiscount.toFixed(2)}</td>
                                            <td>{priceWithoutGst.toFixed(2)}</td>
                                            <td>{gstRate / 2}%</td>
                                            <td>{(priceWithoutGst * (gstRate / 200)).toFixed(2)}</td>
                                            <td>{gstRate / 2}%</td>
                                            <td>{(priceWithoutGst * (gstRate / 200)).toFixed(2)}</td>
                                            <td>{(rowTotal).toFixed(2)}</td>
                                        </tr>
                                    )})}
                                        <tr class="tr-borders">
                                            <td></td>
                                            <td> </td>
                                            <td> </td>
                                            <td>{order.quantity}</td>
                                            <td> </td>
                                            <td>{order.discount}</td>
                                            <td> </td>
                                            <td> </td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td>{(order?.total - order.discount + order.gstAmount).toFixed(2)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <Link class="back-btn theme-bg" to="/orders"> Back</Link>
                    </div>
                </div>
            </div>
        </div>
    </div>

    {/* Write Review Modal */}
    {reviewModalOpen && activeProduct && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1050
      }}>
        <div style={{
          backgroundColor: '#fff',
          padding: '24px',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '450px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          textAlign: 'left'
        }}>
          <h5 style={{ fontWeight: 'bold', marginBottom: '16px', color: '#333' }}>Write a Review</h5>
          <p style={{ fontSize: '14px', color: '#555', marginBottom: '16px' }}>
            <strong>Product:</strong> {activeProduct.name}
          </p>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '4px', color: '#444' }}>Rating</label>
            <div style={{ display: 'flex', gap: '8px', fontSize: '28px', cursor: 'pointer' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  onClick={() => setRating(star)}
                  style={{ color: star <= rating ? '#FFD700' : '#e0e0e0', transition: 'color 0.15s' }}
                >
                  ★
                </span>
              ))}
            </div>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '6px', color: '#444' }}>Comment</label>
            <textarea
              rows="4"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience with this product..."
              className="form-control"
              style={{ resize: 'none', fontSize: '14px' }}
            />
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              className="btn btn-light btn-sm fw-semibold"
              onClick={() => {
                dismissedReviewRef.current = true;
                setReviewModalOpen(false);
                setActiveProduct(null);
                setComment("");
                setRating(5);
              }}
              disabled={submittingReview}
              style={{ border: '1px solid #ccc', fontSize: '13px' }}
            >
              Cancel
            </button>
            <button
              className="btn btn-success btn-sm fw-semibold"
              onClick={handleReviewSubmit}
              disabled={submittingReview}
              style={{ fontSize: '13px', backgroundColor: '#66A96B', borderColor: '#66A96B', color: 'white' }}
            >
              {submittingReview ? 'Submitting...' : 'Submit Review'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Request Return Modal */}
    {returnModalOpen && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1050
      }}>
        <div style={{
          backgroundColor: '#fff',
          padding: '24px',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '450px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          textAlign: 'left'
        }}>
          <h5 style={{ fontWeight: 'bold', marginBottom: '16px', color: '#333' }}>Request Return</h5>
          <p style={{ fontSize: '14px', color: '#555', marginBottom: '16px' }}>
            Please state the reason for requesting a return for Order <strong>#BH{order?.id}</strong>.
          </p>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '6px', color: '#444' }}>Reason for Return</label>
            <textarea
              rows="4"
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              placeholder="E.g., Received wrong product, damaged items, incorrect size..."
              className="form-control"
              style={{ resize: 'none', fontSize: '14px' }}
            />
          </div>

          {order?.orderType === 2 && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '6px', color: '#444' }}>
                UPI ID (For Refund) <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                value={upiId}
                onChange={(e) => setUpiId(e.target.value)}
                placeholder="E.g., username@upi, name@okaxis"
                className="form-control"
                style={{ fontSize: '14px' }}
              />
              <small className="text-muted d-block mt-1" style={{ fontSize: '11px' }}>
                Since this is a Cash on Delivery (COD) order, please provide your UPI ID where the refund can be credited.
              </small>
            </div>
          )}
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              className="btn btn-light btn-sm fw-semibold"
              onClick={() => {
                setReturnModalOpen(false);
                setReturnReason("");
                setUpiId("");
              }}
              disabled={submittingReturn}
              style={{ border: '1px solid #ccc', fontSize: '13px' }}
            >
              Cancel
            </button>
            <button
              className="btn btn-warning btn-sm fw-semibold text-white"
              onClick={handleReturnSubmit}
              disabled={submittingReturn}
              style={{ fontSize: '13px' }}
            >
              {submittingReturn ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </div>
      </div>
    )}
</main>
    </>
  );
};
